import { useCallback, useContext, useState } from 'react';
import { useHistory } from 'react-router';

import { postIncodeVerificationProgress } from 'PrivateApis';

import { UserContext } from 'Context/user';
import { VehicleLookupContext } from 'Context/vehicleLookup';

import PageLoader from 'Components/transitions/PageLoader/PageLoader';

import { INCODE_ANALYTICS } from '../IncodeAnalytics';
import type { IncodeProgressStatus, IncodeStepProps } from '../IncodeSDKHelpers';
import { INCODE_BE_ROUTES, INCODE_PROGRESS_STATUS, INCODE_ROUTES, SENTRY_TAG, useIncodeSlug } from '../IncodeSDKHelpers';

import styles from '../IncodeSdkPage.scss';

const EMPTY_RESPONSE = { data: {} };

interface PostIncodeProgressParams {
  abortController?: AbortController;
  fallbackNextStep: typeof INCODE_ROUTES[keyof typeof INCODE_ROUTES];
  incodeSession: IncodeStepProps['incodeSession'];
  status: IncodeProgressStatus['status'];
  step: IncodeProgressStatus['step'];
}

export const usePostIncodeProgress = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { userState } = useContext(UserContext);
  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);

  const history = useHistory();
  const slug = useIncodeSlug();

  const BASE_URL = `/${vehicle.vrm}${slug}`;

  const postIncodeProgress = useCallback(async (
    session: IncodeStepProps['incodeSession'],
    status: IncodeProgressStatus,
    abortController?: AbortController,
  ) => {
    if (!session?.interviewId || !status) {
      return EMPTY_RESPONSE;
    }

    const progress = await postIncodeVerificationProgress(
      session.id,
      { Authorization: userState?.auth_token },
      { interviewId: session.interviewId, ...status },
      abortController,
    ).catch((err) => {
      globalThis?.Sentry?.captureException?.(err, SENTRY_TAG);
      return EMPTY_RESPONSE;
    });

    return progress.data;
  }, [userState?.auth_token]);

  const postIncodeComplete = useCallback(async ({
    abortController,
    fallbackNextStep,
    incodeSession,
    status,
    step,
  }: PostIncodeProgressParams) => {
    setIsLoading(true);

    if (status === INCODE_PROGRESS_STATUS.FAILED) {
      window?.Sentry?.captureException(new Error('Incode step failed'), {
        tags: {
          ...SENTRY_TAG.tags,
          incodeSession,
          status,
          step,
        },
      });
    }

    INCODE_ANALYTICS.API_TRIGGERED({
      action: 'post_incode_progress',
      backend_entity: 'incode_progress',
      backend_service: 'incode_service',
      success: status === INCODE_PROGRESS_STATUS.SUCCESS,
      value: step,
    });

    const { allStepsCompleted, nextStep }: {
      allStepsCompleted: boolean;
      nextStep?: IncodeProgressStatus['step'];
    } = await postIncodeProgress(
      incodeSession,
      { status, step },
      abortController,
    );

    history.replace(allStepsCompleted
      ? `${BASE_URL}${INCODE_ROUTES.COMPLETE}`
      : `${BASE_URL}${(nextStep && INCODE_BE_ROUTES[nextStep]) ?? fallbackNextStep}`);
  }, [BASE_URL, history, postIncodeProgress]);

  const IncodeProgressLoading = isLoading
    ? () => <PageLoader className={styles.pageLoader} />
    : () => null;

  return { IncodeProgressLoading, postIncodeComplete, postIncodeProgress };
};
